import type { ServerNode } from "./types";
import type { ConditionNodeData, BusinessHoursDayConfig } from "../nodes/ConditionNode";
import { conditionCodeToTitle } from "../utils/conditionCodeMapping";
import { castToString, castToStringArray } from "./configCastHelpers";

function castBusinessDays(value: unknown): BusinessHoursDayConfig[] | undefined {
    if (!Array.isArray(value)) return undefined;
    return value.filter((v): v is BusinessHoursDayConfig => v != null && typeof v === "object") as BusinessHoursDayConfig[];
}

/**
 * Converts a server Condition.* node into a ConditionNodeData object
 * that the existing ConditionNode component and ConditionConfigPanel can consume.
 *
 * Field-name translations (server → UI):
 *   config.matchMode  → matchType
 *   config.schedule   → runSchedule
 *   config.numbers    → phoneNumbers
 */
export function hydrateConditionData(node: ServerNode): ConditionNodeData {
    const { config, type } = node;

    const base: ConditionNodeData = {
        conditionCode: type,
        isPlaceholder: false,
        title: conditionCodeToTitle(type) ?? "When to continue",
    };

    switch (type) {
        case "Condition.Message.KeywordMatch":
            return {
                ...base,
                keywords: castToStringArray(config.keywords),
                matchType: castToString(config.matchMode) ?? "Contains",
            };

        case "Condition.Time.RunSchedule": {
            const runSchedule = castToString(config.schedule) ?? "24_7";
            const hydrated: ConditionNodeData = {
                ...base,
                runSchedule,
            };
            if (runSchedule === "SpecificHours" && config.businessDays && config.timeZone) {
                const businessDays = castBusinessDays(config.businessDays);
                if (businessDays?.length) {
                    hydrated.businessHours = {
                        timeZone: castToString(config.timeZone),
                        businessDays,
                    };
                }
            }
            return hydrated;
        }

        case "Condition.Location.Countries":
            return {
                ...base,
                countries: castToStringArray(config.countries),
            };

        case "Condition.Message.NumberMatch":
            return {
                ...base,
                phoneNumbers: castToStringArray(config.numbers),
            };

        default:
            return base;
    }
}
